import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import SubPageLayout from "../../../components/shared/SubPageLayout";
import HoldingsTable from "../../../components/allocations/HoldingsTable";
import AssetAllocationChart from "../../../components/allocations/AssetAllocationChart";
import SectorAllocationChart from "../../../components/allocations/SectorAllocationChart";
import PerformanceChart from "../../../components/allocations/PerformanceChart";
import useClientDatabase from "../../../hooks/useClientDatabase";
import useHouseholdHoldings from "../../../hooks/useHouseholdHoldings";
import { getHoldings } from "../../../utils/apiConfig";
import { FaSort, FaSortUp, FaSortDown } from "react-icons/fa";
import SymbolDetailModal from "../../../components/allocations/SymbolDetailModal";
import { useTheme } from '../../../contexts/ThemeContext';

import logger from '../../../utils/logger';

const HouseholdView = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const currentPath = location.pathname;
  const { households, loading, error } = useClientDatabase();
  const [expanded, setExpanded] = useState(null); // householdId
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const itemsPerPage = 10;
  const [prices, setPrices] = useState({});
  const [householdTotals, setHouseholdTotals] = useState({});
  const [loadingTotals, setLoadingTotals] = useState(false);
  const [sortField, setSortField] = useState("lastName");
  const [sortDirection, setSortDirection] = useState("asc"); // 'asc' or 'desc'
  const [selectedSymbol, setSelectedSymbol] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const { theme } = useTheme();

  // Helper: get the sort key for last name from backend last_name field
  function getSortLastName(hh) {
    if (hh.last_name) {
      // If multiple last names (e.g., 'Smith & Johnson'), split and use the one closest to A
      const names = hh.last_name.split("&").map((s) => s.trim().toLowerCase());
      return names.sort()[0] || "";
    }
    // Fallback: last word of householdName
    if (hh.householdName) {
      const words = hh.householdName.trim().split(" ");
      return words[words.length - 1].toLowerCase();
    }
    return "";
  }

  // Sorting logic
  function sortHouseholds(list) {
    return [...list].sort((a, b) => {
      let aVal, bVal;
      switch (sortField) {
        case "lastName":
          aVal = getSortLastName(a);
          bVal = getSortLastName(b);
          break;
        case "totalMarketValue":
          aVal = householdTotals[a.householdId] ?? 0;
          bVal = householdTotals[b.householdId] ?? 0;
          break;
        case "accountCount":
          aVal = a.accounts.length;
          bVal = b.accounts.length;
          break;
        default:
          aVal = getSortLastName(a);
          bVal = getSortLastName(b);
      }
      if (aVal < bVal) return sortDirection === "asc" ? -1 : 1;
      if (aVal > bVal) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });
  }

  // Memoized sorted and filtered households
  const sortedHouseholds = useMemo(() => {
    return sortHouseholds(households);
  }, [households, sortField, sortDirection, householdTotals]);

  const filteredHouseholds = useMemo(() => {
    return sortedHouseholds.filter(
      (hh) =>
        !search ||
        (hh.householdName || "").toLowerCase().includes(search.toLowerCase()),
    );
  }, [sortedHouseholds, search]);

  const pagedHouseholds = useMemo(() => {
    return filteredHouseholds.slice(
      (page - 1) * itemsPerPage,
      page * itemsPerPage,
    );
  }, [filteredHouseholds, page, itemsPerPage]);

  // Always call the hook, but only use the data for the expanded row
  const expandedHoldings = useHouseholdHoldings(expanded);

  // Helper: fetch holdings for a household
  async function fetchHoldingsForHousehold(householdId) {
    try {
      const res = await getHoldings({ household_id: householdId });
      return res.data || [];
    } catch {
      return [];
    }
  }

  // Helper: fetch prices for a set of symbols
  async function fetchPricesForSymbols(symbols) {
    try {
      const resp = await fetch("/data/all_holdings_weekly_closes.json");
      const json = await resp.json();
      const latestPrices = {};
      for (const symbol of symbols) {
        const arr = json[symbol];
        if (arr && arr.length > 0) {
          latestPrices[symbol] = arr[arr.length - 1][1];
        }
      }
      return latestPrices;
    } catch {
      return {};
    }
  }

  // Calculate totals for a batch of households
  async function calculateTotalsForHouseholds(householdBatch) {
    const totals = {};
    const allSymbols = new Set();
    const holdingsMap = {};
    // 1. Fetch holdings for each household
    await Promise.all(
      householdBatch.map(async (hh) => {
        const holdings = await fetchHoldingsForHousehold(hh.householdId);
        holdingsMap[hh.householdId] = holdings;
        holdings.forEach((h) => {
          if (h.symbol) allSymbols.add(h.symbol);
        });
      }),
    );
    // 2. Fetch prices for all unique symbols
    const priceMap = await fetchPricesForSymbols(Array.from(allSymbols));
    // 3. Calculate totals
    for (const hh of householdBatch) {
      const holdings = holdingsMap[hh.householdId] || [];
      let total = 0;
      holdings.forEach((h) => {
        const price = priceMap[h.symbol] != null ? priceMap[h.symbol] : null;
        let marketValue = null;
        if (price != null && !isNaN(price)) {
          marketValue = (parseFloat(h.quantity) || 0) * price;
        } else if (h.market_value != null && !isNaN(h.market_value)) {
          marketValue = parseFloat(h.market_value);
        } else {
          marketValue = 0;
        }
        total += marketValue;
      });
      totals[hh.householdId] = total;
    }
    return { totals, priceMap };
  }

  // On initial page load, calculate totals for first page (only for households missing totals)
  useEffect(() => {
    let cancelled = false;
    async function loadFirstPageTotals() {
      setLoadingTotals(true);
      // Only fetch for households missing totals
      const batch = pagedHouseholds.filter(
        (hh) => householdTotals[hh.householdId] === undefined,
      );
      if (batch.length === 0) {
        setLoadingTotals(false);
        return;
      }
      const { totals, priceMap } = await calculateTotalsForHouseholds(batch);
      if (!cancelled) {
        setHouseholdTotals((prev) => ({ ...prev, ...totals }));
        setPrices((prev) => ({ ...prev, ...priceMap }));
        setLoadingTotals(false);
      }
    }
    loadFirstPageTotals();
    return () => {
      cancelled = true;
    };
  }, [
    page,
    search,
    itemsPerPage,
    pagedHouseholds.map((hh) => hh.householdId).join(),
    householdTotals,
  ]);

  // After first page, background load the rest (only for households missing totals)
  useEffect(() => {
    let cancelled = false;
    async function loadBackgroundTotals() {
      const rest = filteredHouseholds
        .slice(itemsPerPage)
        .filter((hh) => householdTotals[hh.householdId] === undefined);
      if (rest.length === 0) return;
      const { totals, priceMap } = await calculateTotalsForHouseholds(rest);
      if (!cancelled) {
        setHouseholdTotals((prev) => ({ ...prev, ...totals }));
        setPrices((prev) => ({ ...prev, ...priceMap }));
      }
    }
    if (!loadingTotals) loadBackgroundTotals();
    return () => {
      cancelled = true;
    };
  }, [filteredHouseholds.length, itemsPerPage, loadingTotals, householdTotals]);

  // Helper: clean household name for display (remove 'Household')
  function getDisplayHouseholdName(hh) {
    if (!hh.householdName) return "";
    return hh.householdName.replace(/\s*Household\s*$/i, "").trim();
  }

  // Helper: get account display name
  function getAccountDisplayName(h) {
    if (!h) return "";
    const first = h.first_name || "";
    const last = h.last_name || "";
    const secFirst = h.secondary_first_name || "";
    const secLast = h.secondary_last_name || "";
    const accountType = h.account_type_name || "";
    const taxQual = h.tax_qualification_name || "";
    let name = `${first} ${last}`.trim();
    if (secFirst || secLast) {
      const secName = `${secFirst} ${secLast}`.trim();
      if (secName) name += ` & ${secName}`;
    }
    if (accountType) {
      if (taxQual && !accountType.toLowerCase().includes(taxQual.toLowerCase())) {
        name += ` ${accountType} (${taxQual})`;
      } else {
        name += ` ${accountType}`;
      }
    } else if (taxQual) {
      name += ` (${taxQual})`;
    }
    return name.trim() || "Unknown Account";
  }

  // Table columns
  const columns = [
    {
      Header: "Household Name",
      accessor: "householdName",
      sortable: true,
      sortKey: "lastName",
      renderSortIcon: () =>
        sortField === "lastName" ? (
          sortDirection === "asc" ? (
            <FaSortUp className="inline ml-1" />
          ) : (
            <FaSortDown className="inline ml-1" />
          )
        ) : (
          <FaSort className="inline ml-1" />
        ),
    },
    {
      Header: "# Members",
      accessor: "memberCount",
      Cell: (row) => row.members.length,
    },
    {
      Header: "# Accounts",
      accessor: "accountCount",
      Cell: (row) => row.accounts.length,
      sortable: true,
      sortKey: "accountCount",
      renderSortIcon: () =>
        sortField === "accountCount" ? (
          sortDirection === "asc" ? (
            <FaSortUp className="inline ml-1" />
          ) : (
            <FaSortDown className="inline ml-1" />
          )
        ) : (
          <FaSort className="inline ml-1" />
        ),
    },
    {
      Header: "Total Market Value",
      accessor: "totalMarketValue",
      Cell: (row) => {
        const value = householdTotals[row.householdId];
        return value !== undefined
          ? `$${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
          : "-";
      },
      sortable: true,
      sortKey: "totalMarketValue",
      renderSortIcon: () =>
        sortField === "totalMarketValue" ? (
          sortDirection === "asc" ? (
            <FaSortUp className="inline ml-1" />
          ) : (
            <FaSortDown className="inline ml-1" />
          )
        ) : (
          <FaSort className="inline ml-1" />
        ),
    },
  ];

  // Debug: print the order of the 10 households on the first page by display name, only if all have last_name
  useEffect(() => {
    if (
      pagedHouseholds.length > 0 &&
      pagedHouseholds.every(
        (hh) => typeof hh.last_name === "string" && hh.last_name.length > 0,
      )
    ) {
      logger.info(
        "[HouseholdView] First page order:",
        pagedHouseholds.map((hh) => getDisplayHouseholdName(hh)),
      );
    }
  }, [pagedHouseholds]);

  const totalPages = Math.ceil(filteredHouseholds.length / itemsPerPage);

  // Utility: Convert allocation map to Chart.js data format
  function allocationToChartData(allocationMap, palette) {
    const labels = Object.keys(allocationMap || {});
    const data = Object.values(allocationMap || {});
    return {
      labels,
      datasets: [
        {
          data,
          backgroundColor: palette || [
            "#8B0000",
            "#4682B4",
            "#CD853F",
            "#6495ED",
            "#800080",
            "#90EE90",
            "#FFD700",
            "#FF69B4",
            "#A0522D",
            "#2E8B57",
          ],
        },
      ],
    };
  }

  const navButtons = [
    { label: "Firm Allocations", route: "/portfolio-management/firm-allocations" },
    { label: "Advisor View", route: "/portfolio-management/advisor-allocations" },
    { label: "Household View", route: "/portfolio-management/household-allocations" },
    { label: "Account View", route: "/portfolio-management/account-allocations" },
  ];

  const breadcrumbItems = [
    { label: "Portfolio Management", link: "/portfolio-management" },
    {
      label: "Household Allocations",
      link: "/portfolio-management/household-allocations",
    },
  ];

  return (
    <SubPageLayout
      title="Household Allocations"
      subtitle="View and analyze household-level portfolio allocations"
      breadcrumbItems={breadcrumbItems}
    >
      {/* Navigation Buttons */}
      <div className="flex space-x-4 mb-6">
        {navButtons.map((btn) => (
          <button
            key={btn.route}
            className={`px-4 py-2 rounded-lg font-medium transition-colors duration-150 ${
              currentPath === btn.route 
                ? "bg-blue-600 text-white dark:bg-blue-500" 
                : "bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600"
            }`}
            onClick={() => navigate(btn.route)}
            disabled={currentPath === btn.route}
          >
            {btn.label}
          </button>
        ))}
      </div>

      {/* Search and Filters */}
      <div className="mb-4 flex justify-between items-center">
        <div className="flex-1 max-w-sm">
          <input
            type="text"
            placeholder="Search households..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
          />
        </div>
        <div className="flex items-center space-x-4 text-sm text-gray-600 dark:text-gray-400">
          <span>
            {filteredHouseholds.length} household{filteredHouseholds.length !== 1 ? "s" : ""}
          </span>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setPage(Math.max(1, page - 1))}
              disabled={page === 1}
              className="px-3 py-1 rounded border border-gray-300 dark:border-gray-600 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 dark:hover:bg-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300"
            >
              Previous
            </button>
            <span className="px-2">
              Page {page} of {Math.ceil(filteredHouseholds.length / itemsPerPage)}
            </span>
            <button
              onClick={() =>
                setPage(
                  Math.min(
                    Math.ceil(filteredHouseholds.length / itemsPerPage),
                    page + 1,
                  ),
                )
              }
              disabled={
                page >= Math.ceil(filteredHouseholds.length / itemsPerPage)
              }
              className="px-3 py-1 rounded border border-gray-300 dark:border-gray-600 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 dark:hover:bg-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300"
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-900">
            <tr>
              <th className="w-8 px-2 py-2"></th>
              {columns.map((col) => (
                <th
                  key={col.accessor}
                  className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider hover:text-gray-700 dark:hover:text-gray-200"
                  style={{ cursor: col.sortable ? "pointer" : "default" }}
                  onClick={() => {
                    if (!col.sortable) return;
                    if (sortField === col.sortKey) {
                      setSortDirection((d) => (d === "asc" ? "desc" : "asc"));
                    } else {
                      setSortField(col.sortKey);
                      setSortDirection("asc");
                    }
                  }}
                >
                  <div className="flex items-center space-x-1">
                    <span>{col.Header}</span>
                    {col.sortable && (
                      <span className="text-gray-400 dark:text-gray-500">
                        {sortField === col.sortKey ? (
                          sortDirection === "asc" ? (
                            <FaSortUp />
                          ) : (
                            <FaSortDown />
                          )
                        ) : (
                          <FaSort />
                        )}
                      </span>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {pagedHouseholds.map((hh) => (
              <React.Fragment key={hh.householdId}>
                <tr className="hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer">
                  <td className="px-2 py-2">
                    <button
                      onClick={() =>
                        setExpanded(
                          expanded === hh.householdId ? null : hh.householdId,
                        )
                      }
                      className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                    >
                      {expanded === hh.householdId ? "▼" : "▶"}
                    </button>
                  </td>
                  {columns.map((col) => (
                    <td
                      key={col.accessor}
                      className="px-4 py-2 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100"
                    >
                      {col.accessor === "householdName"
                        ? getDisplayHouseholdName(hh)
                        : col.Cell
                          ? col.Cell(hh)
                          : hh[col.accessor]}
                    </td>
                  ))}
                </tr>
                {expanded === hh.householdId && expandedHoldings && (
                  <tr>
                    <td colSpan={columns.length + 1} className="bg-gray-50 dark:bg-gray-800/50">
                      {/* Error state */}
                      {expandedHoldings.error ? (
                        <div className="p-4 bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-400 rounded mb-4">
                          Unable to load holdings for this household: {expandedHoldings.error}
                        </div>
                      ) : expandedHoldings.loading ? (
                        <div className="p-4 text-gray-500 dark:text-gray-400 italic">
                          Loading holdings…
                        </div>
                      ) : (
                        <div className="p-4">
                          {/* Household and Account Balances Summary */}
                          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4">
                            {(() => {
                              const accountBalances = {};
                              expandedHoldings.holdings.forEach((h) => {
                                const symbol = h.symbol;
                                const price = prices[symbol] != null ? prices[symbol] : null;
                                let marketValue = null;
                                if (price != null && !isNaN(price)) {
                                  marketValue = (parseFloat(h.quantity) || 0) * price;
                                } else if (h.market_value != null && !isNaN(h.market_value)) {
                                  marketValue = parseFloat(h.market_value);
                                } else {
                                  marketValue = 0;
                                }
                                if (!accountBalances[h.account_number]) {
                                  accountBalances[h.account_number] = 0;
                                }
                                accountBalances[h.account_number] += marketValue;
                              });

                              const accountList = Object.entries(accountBalances);
                              const totalHouseholdBalance = accountList.reduce(
                                (sum, [, balance]) => sum + balance,
                                0
                              );

                              if (accountList.length === 0) {
                                return (
                                  <div className="text-gray-500 dark:text-gray-400 italic">
                                    No accounts or holdings found for this household.
                                  </div>
                                );
                              }

                              return (
                                <>
                                  <div className="flex justify-between items-center mb-4">
                                    <div className="font-semibold text-lg text-gray-900 dark:text-gray-100">
                                      Total Household Balance:{" "}
                                      <span className="text-blue-600 dark:text-blue-400">
                                        ${totalHouseholdBalance.toLocaleString(undefined, {
                                          minimumFractionDigits: 2,
                                          maximumFractionDigits: 2,
                                        })}
                                      </span>
                                    </div>
                                    <button
                                      className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 hover:underline text-sm font-medium"
                                      title="View all accounts for this household"
                                      onClick={() =>
                                        navigate(
                                          `/portfolio-management/household-allocations/${hh.householdId}`
                                        )
                                      }
                                    >
                                      View All Accounts
                                    </button>
                                  </div>
                                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                    <thead>
                                      <tr>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                          Account #
                                        </th>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                          Account Name
                                        </th>
                                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                          Balance
                                        </th>
                                      </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                                        {accountList.map(([accNum, balance]) => {
                                          const account = expandedHoldings.holdings.find(
                                            (h) => h.account_number === accNum
                                          );
                                          return (
                                            <tr
                                              key={accNum}
                                              className="hover:bg-gray-50 dark:hover:bg-gray-700/50"
                                            >
                                              <td className="px-4 py-2 whitespace-nowrap text-sm">
                                                <span 
                                                  className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 hover:underline cursor-pointer" 
                                                  onClick={() => navigate(`/portfolio-management/household-allocations/${hh.householdId}`)}
                                                  title="View Account Details"
                                                >
                                                  {accNum}
                                                </span>
                                              </td>
                                              <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                                                {getAccountDisplayName(account)}
                                              </td>
                                              <td className="px-4 py-2 whitespace-nowrap text-sm text-right text-gray-900 dark:text-gray-100">
                                                ${balance.toLocaleString(undefined, {
                                                  minimumFractionDigits: 2,
                                                  maximumFractionDigits: 2,
                                                })}
                                              </td>
                                            </tr>
                                          );
                                        })}
                                      </tbody>
                                  </table>
                                </>
                              );
                            })()}
                          </div>
                        </div>
                      )}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </SubPageLayout>
  );
};

export default HouseholdView;
